import { useRuntimeConfig } from '#imports'

import type {
  MultipleQueriesQuery,
  SearchResponse,
} from '@algolia/client-search'
import algoliasearch from 'algoliasearch'

export type DefaultAlgoliaResults = { results: { hits: unknown[] }[] }

export type AlgoliaSearchFn<T = unknown> = (
  queries: MultipleQueriesQuery[],
) => Promise<{ results: Array<SearchResponse<T>> }>

export type InitializableAlgoliaClient<T> = (
  id: string,
  apiKey: string,
) => { search: AlgoliaSearchFn<T> }

export type AlgoliaSearchParams = {
  queries: MultipleQueriesQuery[]
  apiKey: string
}

/**
 * This allows to inject a fake algolia client to easily test the feature
 * - without using the Algolia library (and making http calls)
 * - without mocking imports
 * @algolia
 * @returns
 */
export function useAlgoliaWithDeps<T>({
  initializableAlgoliaClient,
}: {
  initializableAlgoliaClient: InitializableAlgoliaClient<T>
}) {
  const { ALGOLIA_ID } = useRuntimeConfig().public

  function search(params: AlgoliaSearchParams) {
    const algoliaClient = initializableAlgoliaClient(ALGOLIA_ID, params.apiKey)

    return algoliaClient.search(params.queries)
  }

  return { search }
}

export function useAlgolia<T>() {
  return useAlgoliaWithDeps({
    initializableAlgoliaClient: algoliasearch as InitializableAlgoliaClient<T>,
  })
}
